import React, { useState, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  getSummary,
  getCategoryBreakdown,
  getMonthlyTrend,
  SummaryResponse,
  CategoryBreakdown,
  MonthlyTrend,
} from '../services/pocketsmith.service';
import { tw, getChartColor } from '../utils/terminalTheme';
import { FiBarChart2, FiTrendingUp } from 'react-icons/fi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const monoFont = "'JetBrains Mono', 'Fira Code', monospace";

export const PocketSmithAnalytics: React.FC = () => {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryBreakdown[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyTrend[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [accountGroup, setAccountGroup] = useState('');
  const [excludeTransfers, setExcludeTransfers] = useState(true);
  const [months, setMonths] = useState(12);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, catRes, monthRes] = await Promise.all([
        getSummary(),
        getCategoryBreakdown({
          account_group: accountGroup || undefined,
          exclude_transfers: excludeTransfers,
        }),
        getMonthlyTrend({
          months,
          account_group: accountGroup || undefined,
          exclude_transfers: excludeTransfers,
        }),
      ]);
      setSummary(sumRes);
      setCategoryData(catRes.data || []);
      setMonthlyData(monthRes.data || []);
    } catch (err) {
      console.error('Analytics load failed', err);
    } finally {
      setLoading(false);
    }
  }, [accountGroup, excludeTransfers, months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sort categories by absolute total, top 15
  const sortedCategories = [...categoryData]
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
    .slice(0, 15);

  const totalIncome = monthlyData.reduce((s, m) => s + (m.income || 0), 0);
  const totalExpenses = monthlyData.reduce((s, m) => s + Math.abs(m.expenses || 0), 0);
  const totalNet = totalIncome - totalExpenses;

  // --- Chart configs ---
  const categoryChartData = {
    labels: sortedCategories.map(c => c.category || 'Uncategorized'),
    datasets: [{
      label: 'Total ($)',
      data: sortedCategories.map(c => Math.abs(c.total)),
      backgroundColor: sortedCategories.map(c => getChartColor(c.category)),
      borderColor: sortedCategories.map(c => getChartColor(c.category)),
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 24,
    }],
  };

  const categoryChartOptions: any = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#22d3ee',
        bodyColor: '#d1d5db',
        borderColor: '#374151',
        borderWidth: 1,
        titleFont: { family: monoFont },
        bodyFont: { family: monoFont },
        callbacks: {
          label: (ctx: any) => `$${ctx.parsed.x.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#6b7280',
          font: { family: monoFont, size: 11 },
          callback: (val: number) => val >= 1000 ? '$' + (val / 1000).toFixed(0) + 'k' : '$' + val,
        },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
      y: {
        ticks: { color: '#d1d5db', font: { family: monoFont, size: 12 } },
        grid: { display: false },
      },
    },
  };

  const monthlyChartData = {
    labels: monthlyData.map(m => {
      const [y, mo] = m.month.split('-');
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return monthNames[parseInt(mo) - 1] + ' ' + y.slice(2);
    }),
    datasets: [
      {
        label: 'Income',
        data: monthlyData.map(m => m.income || 0),
        backgroundColor: 'rgba(34, 197, 94, 0.7)',
        borderColor: '#22c55e',
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Expenses',
        data: monthlyData.map(m => Math.abs(m.expenses || 0)),
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderColor: '#ef4444',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const monthlyChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#9ca3af',
          font: { family: monoFont, size: 12 },
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#22d3ee',
        bodyColor: '#d1d5db',
        borderColor: '#374151',
        borderWidth: 1,
        titleFont: { family: monoFont },
        bodyFont: { family: monoFont },
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#6b7280', font: { family: monoFont, size: 11 } },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
      y: {
        ticks: {
          color: '#6b7280',
          font: { family: monoFont, size: 11 },
          callback: (val: number) => val >= 1000 ? '$' + (val / 1000).toFixed(0) + 'k' : '$' + val,
        },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
    },
  };

  const groupButtons = [
    { key: '', label: 'All', color: 'text-white' },
    { key: 'PERSONAL', label: 'Personal', color: 'text-amber-400' },
    { key: 'BUSINESS', label: 'Business', color: 'text-blue-400' },
    { key: 'INVESTMENT', label: 'Investment', color: 'text-emerald-400' },
  ];

  const fmt = (n: number) => '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: monoFont }}>
          <span className="text-cyan-400">~/</span>pocketsmith/analytics
          <span className="text-green-500 text-base ml-2">$</span>
          <span className="text-gray-500 text-base ml-1">stats --charts</span>
        </h1>
        <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: monoFont }}>
          Category breakdown and monthly spending trends
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-1">
          {groupButtons.map(g => (
            <button
              key={g.key}
              onClick={() => setAccountGroup(g.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
                accountGroup === g.key
                  ? 'bg-gray-700 ' + g.color + ' font-semibold'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setExcludeTransfers(!excludeTransfers)}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
            excludeTransfers
              ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30'
              : 'bg-gray-800/50 text-gray-500 border border-gray-700/30'
          }`}
        >
          {excludeTransfers ? '✓ ' : ''}Hide Transfers
        </button>

        <select
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          className="bg-gray-800/50 border border-gray-700/30 text-gray-300 text-xs font-mono rounded-md px-3 py-1.5"
        >
          <option value={3}>3 months</option>
          <option value={6}>6 months</option>
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
        </select>
      </div>

      {/* Stat Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Total Transactions</div>
            <div className="text-cyan-400 text-xl font-bold font-mono">{summary.total_transactions.toLocaleString()}</div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Matched</div>
            <div className="text-green-400 text-xl font-bold font-mono">{summary.matched.toLocaleString()}</div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Unmatched</div>
            <div className="text-amber-400 text-xl font-bold font-mono">{summary.unmatched.toLocaleString()}</div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Net Cash Flow</div>
            <div className={`text-xl font-bold font-mono ${totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalNet >= 0 ? '+' : '-'}{fmt(totalNet)}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center text-gray-500 py-12 font-mono">Loading analytics...</div>
      )}

      {!loading && (
        <div className="space-y-6">
          {/* Category Breakdown */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 mb-4 px-4 pt-4">
              <FiBarChart2 className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Spending by Category</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">{sortedCategories.length} categories</span>
            </div>
            {sortedCategories.length > 0 ? (
              <div className="px-4 pb-4">
                <div style={{ height: Math.max(300, sortedCategories.length * 36) + 'px' }}>
                  <Bar data={categoryChartData} options={categoryChartOptions} />
                </div>
                {/* Summary table */}
                <div className="mt-4 border-t border-gray-700/30 pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                    {sortedCategories.map(c => (
                      <div key={c.category} className="flex items-center justify-between py-1 text-xs font-mono">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-sm inline-block"
                            style={{ backgroundColor: getChartColor(c.category) }}
                          />
                          <span className="text-gray-300">{c.category || 'Uncategorized'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-500">{c.count} txn</span>
                          <span className={c.total >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {c.total >= 0 ? '+' : '-'}{fmt(c.total)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8 font-mono text-sm">No category data</div>
            )}
          </div>

          {/* Monthly Trend */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 mb-4 px-4 pt-4">
              <FiTrendingUp className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Monthly Income vs Expenses</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">{months} months</span>
            </div>
            {monthlyData.length > 0 ? (
              <div className="px-4 pb-4">
                <div style={{ height: '400px' }}>
                  <Bar data={monthlyChartData} options={monthlyChartOptions} />
                </div>
                {/* Net cash flow row */}
                <div className="mt-4 border-t border-gray-700/30 pt-3">
                  <div className="flex flex-wrap gap-3">
                    {monthlyData.map(m => {
                      const [y, mo] = m.month.split('-');
                      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const label = monthNames[parseInt(mo) - 1] + ' ' + y.slice(2);
                      return (
                        <div key={m.month} className="text-center text-xs font-mono">
                          <div className="text-gray-500">{label}</div>
                          <div className={m.net >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {m.net >= 0 ? '+' : ''}{fmt(m.net)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Total */}
                  <div className="mt-3 pt-2 border-t border-gray-700/20 flex justify-between items-center">
                    <span className="text-gray-400 text-xs font-mono">Total Net</span>
                    <span className={`text-sm font-bold font-mono ${totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totalNet >= 0 ? '+' : '-'}{fmt(totalNet)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8 font-mono text-sm">No monthly data</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
